import React, { useEffect, useState } from "react";
import { Link, Route, Routes } from "react-router-dom";
import { useHistory, useRouteMatch, RenderRoute } from "../../utils/router";
import Select from "react-select";
import SelectAsync from "react-select/async";
import dayjs from "dayjs";

import Pagination from "../general/Pagination";
import Modal from "../general/Modal";
import Create from "./Create";
import Edit from "./Edit";
import Loader from "../general/Loader";
import { formatCurrency, getLink, permisions } from "../../utils/helpers";
import { apiRequest } from "../../hooks/apiRequest";
import { auth } from "../../utils/auth";
import { ESTADOS_CHEQUE } from "../../utils/types";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";

export const renderEstadoBadge = (estado) => {
    const opcion = ESTADOS_CHEQUE.find((e) => e.value === estado);

    if (!opcion) {
        return <span className="badge badge-danger p-2">Rechazado</span>;
    }

    return <span className={`badge badge-${opcion.badge} p-2`}>{opcion.label}</span>;
};

const formatFecha = (fecha) => (fecha ? dayjs(fecha).format("DD/MM/YYYY") : "");

/**
 * Una fila por cheque físico. Si el cheque cubrió varias facturas o compras, su
 * detalle viene en `documentos` (antes llegaba como N filas y se agrupaban aquí,
 * lo que partía los grupos entre páginas).
 */
const FilaCheque = ({ cheque, onEditar }) => {
    const documentos = cheque.documentos || [];
    const multiple = documentos.length > 1;
    const fechaEvento = cheque.status === "válido" ? cheque.validadoAt : cheque.rechazadoAt;

    return (
        <tr className='tr-shadow'>
            <td>
                <div title="Fecha de recibo">
                    <strong>R:</strong> {cheque.recibo ? formatFecha(cheque.recibo.created_at) : formatFecha(cheque.fecha)}
                </div>
                {fechaEvento && (
                    <div title={cheque.status === "válido" ? "Fecha de validación" : "Fecha de anulación"}>
                        <strong>{cheque.status === "válido" ? "V:" : "A:"}</strong> {formatFecha(fechaEvento)}
                    </div>
                )}
            </td>
            <td>
                <strong>{cheque.numero}</strong>
                <div>{cheque.banco}</div>
                <div><small className={`badge badge-${cheque.tipo === 'INGRESO' ? 'success' : 'primary'} badge-small`}>{cheque.tipo}</small></div>
            </td>
            <td>
                {multiple ? documentos.map((documento, i) => (
                    <React.Fragment key={documento.id}>
                        <div className="pb-2">{documento.concepto}</div>
                        {i < documentos.length - 1 && <hr className="m-0 mb-2" />}
                    </React.Fragment>
                )) : cheque.concepto}
                {cheque.recibo?.numeracion && (
                    <div><strong>{cheque.recibo.numeracion}</strong></div>
                )}
            </td>
            <td>
                {multiple && (
                    <>
                        {documentos.map((documento) => (
                            <div className="pb-1" key={documento.id}>{formatCurrency(documento.monto)}</div>
                        ))}
                        <hr className="m-0 mb-2" />
                    </>
                )}
                <div><strong>{formatCurrency(cheque.monto)}</strong></div>
                <small style={{lineHeight: 1.1}}>{cheque.cuenta?.nombre}</small>
            </td>
            <td>
                {cheque.contacto?.nombre}
                {cheque.entregado && (
                    <div>{cheque.tipo === 'INGRESO' ? 'Recibido de:' : 'Entregado a:'} {cheque.entregado}</div>
                )}
            </td>
            <td>
                {renderEstadoBadge(cheque.status)}
                {cheque.historico && (
                    <div className="m-t-5" title="Datos del modelo anterior que no cuadran: solo lectura">
                        <small className="badge badge-secondary badge-small">Histórico</small>
                    </div>
                )}
            </td>
            <td>
                <div className='table-data-feature'>
                    <Link
                        to={getLink(`/cheques/${cheque.id}`)}
                        className='item'
                        title='Editar'
                        onClick={() => onEditar(cheque)}>
                        <i className='zmdi zmdi-edit' />
                    </Link>
                </div>
            </td>
        </tr>
    );
};

const Cheques = () => {
    const [cheques, setCheques] = useState([]);
    const [state, setState] = useState({
        cheque: {},
        links: null,
        meta: null,
        showCreate: false,
        showEdit: false,
        loading: true,
    });

    const now = new Date();
    const startOfMonth = dayjs(new Date(now.getFullYear(), now.getMonth(), 1)).format("YYYY-MM-DD");
    const endOfMonth = dayjs(new Date(now.getFullYear(), now.getMonth() + 1, 0, 23)).format("YYYY-MM-DD");
    const [filtros, setFiltros] = useState({
        buscar: "",
        estado: "procesando",
        desde: startOfMonth,
        hasta: endOfMonth,
    });
    const [query, setQuery] = useState( Object.keys(filtros).map((fil) => {
            return `${fil}=${filtros[fil]}`;
        }));
    const tipos = [
        {value: "INGRESO", label: "Ingreso"},
        {value: "EGRESO", label: "Egreso"},
    ];

    const history = useHistory();
    const match = useRouteMatch();

    const getCheques = async (url) => {
        url = url ? url.split('/').pop() : "cheques?limit=25&" + query.join("&");
        const cheques = await apiRequest(null, url, 'get');
        if (cheques.links) {
            setCheques(cheques.data);

            setState({
                ...state,
                showCreate: false,
                links: cheques.links,
                meta: cheques.meta,
                loading: false,
            });
        }
    };

    const toggleCreateModal = () => {
        setState(state => ({
            ...state,
            showCreate: !state.showCreate,
            cheque: {},
        }));
    };

    const toggleEditModal = (cheque) => {
        setState({
            ...state,
            showEdit: !state.showEdit,
            cheque: cheque,
        });
        history.push(getLink("/cheques"));
    };

    const getOptions = async (url = null, label = 'nombre', value = 'id') => {
        const table = await apiRequest({}, url, 'get');
        if (table.data) {
            return table.data.map(item => ({value: item[value], label: item[label]}));
        }
    };

    // El debounce se reconstruía en cada render, así que cada pulsación
    // estrenaba su propio temporizador y no agrupaba nada. Ahora el filtro entra
    // al estado de inmediato y lo que espera es la petición.
    const onFiltroChange = (value, filtro) =>
        setFiltros((previos) => ({ ...previos, [filtro]: value }));

    useEffect(() => {
        const qry = Object.keys(filtros).map((fil) => {
            return `${fil}=${filtros[fil]}`;
        });

        setQuery(qry);
    }, [filtros]);

    // saltarPrimero en false: esta vista no tiene otra carga inicial, el listado
    // entra por aquí al montar.
    useDebouncedEffect(() => {
        getCheques();
    }, [query], { saltarPrimero: false });

    const { loading } = state;

    return (
        <div className='container'>
            <div className='page-header'>
                <h1 className='page-title row'>
                    <span className='col'>Todos los Cheques</span>
                    {auth.can(permisions.reporteContabilidadCheques) && <Link
                        to={getLink(`/imprimir/cheques?${query.join('&')}`)}
                        target='_blank'
                        className='au-btn-filter m-r-15'>
                        <i className='zmdi zmdi-print' /> Imprimir
                    </Link>}
                    <button
                        className='au-btn au-btn-icon au-btn--green au-btn--small'
                        onClick={toggleCreateModal}>
                        <i className='zmdi zmdi-plus' />
                        Nuevo Cheque
                    </button>
                </h1>
                <div className='page-controls'>
                    <div className='table-data__tool'>
                        <div className='table-data__tool-left row'>
                            <div className='col'>
                                <label htmlFor="">Buscar</label>
                                <input
                                    onChange={(e) =>
                                        onFiltroChange(
                                            e.target.value,
                                            "buscar"
                                        )
                                    }
                                    type='search'
                                    placeholder='Buscar...'
                                    className='form-control'
                                    name='buscar'
                                />
                            </div>
                            <div className='col-md-2'>
                                <label htmlFor="cuenta_id">Cuenta</label>
                                <SelectAsync
                                    key="cuenta"
                                    onChange={(e) =>
                                        onFiltroChange(
                                            e?.value || '',
                                            "cuenta"
                                        )
                                    }
                                    isClearable
                                    placeholder='Seleccionar...'
                                    menuPosition='fixed'
                                    menuPlacement='auto'
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={(e) => getOptions("cuentas?buscar="+e)}
                                />
                            </div>

                            <div className='col-md-2'>
                                <label htmlFor="cuenta_id">Estado</label>
                                <Select
                                    key="estado"
                                    name='estado'
                                    isClearable
                                    placeholder='Seleccionar...'
                                    defaultValue={
                                        {label: "Procesando", value: "procesando"}
                                    }
                                    onChange={(e) =>
                                        onFiltroChange(
                                            e?.value || "",
                                            "estado"
                                        )
                                    }
                                    options={[
                                        {label: "Todas", value: ""},
                                        ...ESTADOS_CHEQUE,
                                    ]}
                                />
                            </div>

                            <div className='col-md-2'>
                                <label htmlFor="cuenta_id">Tipo</label>
                                <Select
                                    key="tipo"
                                    name='tipo'
                                    isClearable
                                    placeholder='Seleccionar...'
                                    onChange={(e) =>
                                        onFiltroChange(
                                            e?.value || "",
                                            "tipo"
                                        )
                                    }
                                    options={[
                                        {label: "Todos", value: ""},
                                        ...tipos,
                                    ]}
                                />
                            </div>

                            <div className='col-md-2'>
                                <label htmlFor="desde">Desde</label>
                                <input
                                    type="date"
                                    name="desde"
                                    id="desde"
                                    className="form-control"
                                    onChange={(e) =>
                                        onFiltroChange(e.target.value, "desde")
                                    }
                                    defaultValue={startOfMonth}
                                />
                            </div>
                            <div className='col-md-2'>
                                <label htmlFor="hasta">Hasta</label>
                                <input
                                    type="date"
                                    id="hasta"
                                    className="form-control"
                                    onChange={(e) =>
                                        onFiltroChange(e.target.value, "hasta")
                                    }
                                    defaultValue={endOfMonth}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='row'>
                <div className='col-md-12'>
                    <div>
                        {loading && <Loader />}
                        {!cheques.length ? (
                            <div>
                                {!loading && (
                                    <div>No hay cheques creados.</div>
                                )}
                            </div>
                        ) : (
                            <div className='table-data'>
                                <table className='table table-data2 cheques-table'>
                                    <thead>
                                        <tr>
                                            <th width="120px">
                                                Fecha
                                            </th>
                                            <th width="200px">Número</th>
                                            <th>Concepto</th>
                                            <th>Monto</th>
                                            <th>Contacto</th>
                                            <th>Estado</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cheques.map((cheque) => (
                                            <FilaCheque
                                                key={cheque.id}
                                                cheque={cheque}
                                                onEditar={toggleEditModal}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                                {state.links && (
                                    <Pagination
                                        links={state.links}
                                        meta={state.meta}
                                        getData={getCheques}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Routes>
                <Route
                    path=':id'
                    element={<RenderRoute render={(props) => (
                        <Modal
                            show
                            blank
                            size="1200px"
                            toggle={toggleEditModal}>
                            <Edit
                                toggle={toggleEditModal}
                                cheque={
                                    cheques.filter(
                                        (r) =>
                                            r.id ===
                                            parseInt(match.params.id)
                                    )[0] || {}
                                }
                                getCheques={getCheques}
                            />
                        </Modal>
                    )} />}
                />
            </Routes>
            <Modal
                show={state.showCreate}
                blank={true}
                size="1200px"
                toggle={toggleCreateModal}>
                <Create
                    toggle={toggleCreateModal}
                    cheque={state.cheque}
                    getCheques={getCheques}
                />
            </Modal>
        </div>
    );
}

export default Cheques;
